import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useIntl } from '../../util/reactIntl';
import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { getListingReviewsApi } from '../../util/api';
import { ensureUser, userDisplayNameAsString } from '../../util/data';
import { normalizeCoachifyImageUrl } from '../../util/coachifyPublicImageUrl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import CustomBookingPanel from './CustomBookingPanel';
import { LoadingPage } from './ListingPage.shared';
import { useSessionTypeDetail } from './hooks/useSessionTypeDetail';
import css from './ListingPage.module.css';

const DEFAULT_COVER_IMAGE =
  'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1600&q=80';

// Public detail page for ONE session type, keyed by the session type's own
// Mongo _id -- NOT the coach's shared Sharetribe shell listingId, which
// every session type for a coach carries identically. Reading straight from
// Mongo (via useSessionTypeDetail) rather than sdk.listings.show also means
// this page never depends on Sharetribe at all. See ListingPageCarousel.js
// for how a session-type id in the URL routes here instead of the
// Sharetribe-driven render tree.
const SessionTypeDetailPage = props => {
  const { sessionTypeId, currentUser, scrollingDisabled, staticContext, onManageDisableScrolling } =
    props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const { sessionType, coachTimezone, availabilityData, loading, error } =
    useSessionTypeDetail(sessionTypeId);

  const [reviews, setReviews] = useState([]);
  const [fetchReviewsError, setFetchReviewsError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (!sessionType?.listingId) {
      return undefined;
    }
    getListingReviewsApi(sessionType.listingId)
      .then(response => {
        if (isMounted) setReviews(response?.items || []);
      })
      .catch(err => {
        console.error('Failed to load reviews:', err);
        if (isMounted) setFetchReviewsError(err);
      });
    return () => {
      isMounted = false;
    };
  }, [sessionType?.listingId]);

  const ensuredAuthor = useMemo(
    () => (sessionType?.author ? ensureUser(sessionType.author) : null),
    [sessionType]
  );
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const reviewCount = reviews.length;
  const rating =
    reviewCount > 0
      ? (reviews.reduce((acc, r) => acc + (r.rating || 0), 0) / reviewCount).toFixed(1)
      : 0;

  const pageTitle = sessionType?.sessionName || 'Session';

  const topbar = <TopbarContainer />;

  if (loading) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (error || !sessionType || !ensuredAuthor) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  const listingStub = {
    id: { uuid: sessionType.listingId },
    type: 'listing',
    author: ensuredAuthor,
    attributes: {
      title: sessionType.sessionName,
      // CheckoutPage derives its transaction process off this alias (and the native
      // booking-engine eligibility branch still gates on it existing), even though the
      // actual booking creation for this page never touches Sharetribe. See
      // ListingPage.shared.js's handleSubmit for the pattern this mirrors.
      publicData: { transactionProcessAlias: 'default-booking/release-1' },
    },
  };

  // CustomBookingPanel builds `values` itself (bookingDates/seats/sessionTypeId/etc) and
  // only calls onSubmit(values) -- without this handler "Book Now" is a silent no-op.
  // Mirrors ListingPage.shared.js's handleSubmit, but uses listingStub directly instead
  // of looking the listing up in the marketplaceData Redux store (there's nothing to find
  // there for a session-type id -- see the loadData short-circuit in ListingPage.duck.js).
  const handleBookingSubmit = values => {
    const {
      bookingDates,
      bookingStartTime,
      bookingEndTime,
      bookingStartDate,
      bookingEndDate,
      priceVariantName,
      quantity: quantityRaw,
      seats: seatsRaw,
      deliveryMethod,
      ...otherOrderData
    } = values;

    const bookingMaybe = bookingDates
      ? { bookingDates: { bookingStart: bookingDates.startDate, bookingEnd: bookingDates.endDate } }
      : bookingStartTime && bookingEndTime
      ? { bookingDates: { bookingStart: bookingStartTime, bookingEnd: bookingEndTime } }
      : {};
    const priceVariantNameMaybe = priceVariantName ? { priceVariantName } : {};
    const quantity = Number.parseInt(quantityRaw, 10);
    const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};
    const seats = Number.parseInt(seatsRaw, 10);
    const seatsMaybe = Number.isInteger(seats) ? { seats } : {};
    const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

    const initialValues = {
      listing: listingStub,
      orderData: {
        ...bookingMaybe,
        ...priceVariantNameMaybe,
        ...quantityMaybe,
        ...seatsMaybe,
        ...deliveryMethodMaybe,
        ...otherOrderData,
      },
      confirmPaymentError: null,
    };

    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routeConfiguration);
    dispatch(setInitialValues(initialValues, !currentUser));
    dispatch(initializeCardPaymentData());

    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routeConfiguration,
        { id: listingStub.id.uuid, slug: createSlug(sessionType.sessionName) },
        {}
      )
    );
  };

  const coverImageUrl = sessionType.sessionImage
    ? normalizeCoachifyImageUrl(sessionType.sessionImage)
    : DEFAULT_COVER_IMAGE;

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled} author={authorDisplayName}>
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.coverImageWrapper}>
          <img src={coverImageUrl} alt={sessionType.sessionName} className={css.coverImage} />
          <div className={css.coverOverlay} />
        </div>
        <div className={css.pageContainer}>
          <div className={css.contentWrapperForProductLayout}>
            <div className={css.mainColumnForProductLayout}>
              <SectionAuthorMaybe
                title={sessionType.sessionName}
                listing={listingStub}
                authorDisplayName={authorDisplayName}
                onContactUser={() => {}}
                isInquiryModalOpen={false}
                onCloseInquiryModal={() => {}}
                sendInquiryError={null}
                sendInquiryInProgress={false}
                onSubmitInquiry={() => {}}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
                sessionCount={1}
                sessionTypes={[sessionType]}
                rating={rating}
                reviewCount={reviewCount}
              />
              <SectionTextMaybe text={sessionType.description} showAsIngress />
              <CustomBookingPanel
                className={css.productOrderPanelFullWidth}
                listing={listingStub}
                author={ensuredAuthor}
                isOwnListing={false}
                currentUser={currentUser}
                onSubmit={handleBookingSubmit}
                sessionTypes={[sessionType]}
                availabilityData={availabilityData}
                coachTimezone={coachTimezone}
                rating={rating}
                reviewCount={reviewCount}
              >
                <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
              </CustomBookingPanel>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default SessionTypeDetailPage;
